// src/components/property/ImageSelectionModal.tsx
/**
 * ImageSelectionModal Component
 * Modal for selecting a source image for a scene
 */

import { memo, useState, useCallback } from 'react';
import { Check, Loader2, ImageIcon } from 'lucide-react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { IImageSelectionModalProps } from '@/@types/propertySceneEditor';
import type { Id } from '../../../convex/_generated/dataModel';

// ============================================================================
// SelectableThumbnail Component
// ============================================================================

interface ISelectableThumbnailProps {
  storageId: Id<'_storage'>;
  isSelected: boolean;
  isCurrent: boolean;
  aspectRatio: '16:9' | '9:16';
  onSelect: () => void;
}

function SelectableThumbnail({
  storageId,
  isSelected,
  isCurrent,
  aspectRatio,
  onSelect,
}: ISelectableThumbnailProps) {
  const imageUrl = useQuery(api.storage.getUrl, { storageId });

  // Use aspect-video (16:9) or aspect-[9/16] (9:16) based on selected ratio
  const aspectClass = aspectRatio === '9:16' ? 'aspect-[9/16]' : 'aspect-video';

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'relative rounded-lg overflow-hidden border-2 transition-all',
        aspectClass,
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        isSelected
          ? 'border-primary ring-2 ring-primary/30'
          : 'border-transparent hover:border-muted-foreground/30'
      )}
    >
      {imageUrl ? (
        <img src={imageUrl} alt="Property image" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
          <div className="bg-primary text-primary-foreground rounded-full p-2">
            <Check className="h-5 w-5" />
          </div>
        </div>
      )}

      {/* Current image badge */}
      {isCurrent && (
        <Badge variant="secondary" className="absolute top-2 left-2 text-xs">
          Current
        </Badge>
      )}
    </button>
  );
}

// ============================================================================
// Empty State Component
// ============================================================================

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
      <p className="text-muted-foreground">No processed images available</p>
      <p className="text-sm text-muted-foreground mt-1">
        Process images in the previous step to select them here
      </p>
    </div>
  );
}

// ============================================================================
// Main ImageSelectionModal Component
// ============================================================================

function ImageSelectionModal({
  isOpen,
  onClose,
  scene,
  processedImages,
  aspectRatio,
  onSelect,
}: IImageSelectionModalProps) {
  // Track selected image (starts with current image if available)
  const currentImageId = scene?.selectedImageId ?? scene?.sourceImageId;
  const [selectedImageId, setSelectedImageId] = useState<Id<'_storage'> | null>(
    currentImageId ?? null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset selection when modal opens with a different scene
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      } else if (scene) {
        setSelectedImageId(scene.selectedImageId ?? scene.sourceImageId ?? null);
      }
    },
    [onClose, scene]
  );

  // Handle image selection
  const handleSelectImage = useCallback((imageId: Id<'_storage'>) => {
    setSelectedImageId(imageId);
  }, []);

  // Handle confirm
  const handleConfirm = useCallback(async () => {
    if (!selectedImageId) return;

    setIsSubmitting(true);
    try {
      await onSelect(selectedImageId);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedImageId, onSelect, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setSelectedImageId(currentImageId ?? null);
    onClose();
  }, [currentImageId, onClose]);

  const hasImages = processedImages.length > 0;
  const isSelectionChanged = selectedImageId !== currentImageId;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Source Image</DialogTitle>
          <DialogDescription>
            Choose an image to use as the source for{' '}
            {scene ? `Scene ${scene.sceneNumber}` : 'this scene'}
          </DialogDescription>
        </DialogHeader>

        {/* Image Grid */}
        <div className="flex-1 overflow-y-auto py-4">
          {hasImages ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {processedImages.map(imageId => (
                <SelectableThumbnail
                  key={imageId}
                  storageId={imageId}
                  isSelected={selectedImageId === imageId}
                  isCurrent={currentImageId === imageId}
                  aspectRatio={aspectRatio}
                  onSelect={() => handleSelectImage(imageId)}
                />
              ))}
            </div>
          ) : (
            <EmptyState />
          )}
        </div>

        {/* Footer */}
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedImageId || isSubmitting || !isSelectionChanged}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Confirm Selection'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Exports
// ============================================================================

export const ImageSelectionModalMemo = memo(ImageSelectionModal);
